import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { StateService } from '@app/service/state/state.service';
import { RouteComponent } from '../../route.component';
import {
  UpdateConnectionWebFormReponse,
  UserService,
} from '@app/service/user-service/user.service';
import { HeaderService } from '@app/service/header/header.service';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { delay, filter, repeatWhen, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-de-finapi-update',
  templateUrl: './de-finapi-update.component.html',
  styleUrls: ['../../page-shared.css', './de-finapi-update.component.scss'],
})
export class FinAPIUpdateComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  loading: boolean = true;
  error: boolean = false;
  webFormId: string = '';
  navigationInProgress: boolean = false;

  ftsDiv: any;
  headScript: any;
  bodyScript: any;
  pollingSubscription: Subscription;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private userService: UserService,
    private location: Location
  ) {
    super(router, route, stateService, headerService);
  }

  ngOnInit() {
    this.setup();
  }

  ngOnDestroy(): void {
    this.removeScript();
  }

  async setup(): Promise<void> {
    await this.fetchUpdatedFinapiConnectionWebform();
  }

  fetchUpdatedFinapiConnectionWebform(): void {
    this.userService.getFinapiUpdateConnectionWebform().subscribe({
      next: (response: UpdateConnectionWebFormReponse) => {
        if (!response || (!response.taskId && !response.webformId)) {
          this.error = true;
          this.loading = false;
          return;
        }

        if (response.webformId) {
          this.webFormId = response.webformId;
          this.loadFinApiHeadScript(response.webformId);
        } else {
          this.pollForFinapiUpdateConnectionWebform(response.taskId);
        }
      },
      error: (error: HttpErrorResponse) => {
        console.log(error);
        this.error = true;
        this.loading = false;
      },
    });
  }

  pollForFinapiUpdateConnectionWebform(responseTaskId: string): void {
    this.pollingSubscription = this.userService
      .pollForFinapiUpdateConnectionWebform(responseTaskId)
      .pipe(
        repeatWhen((obs) => obs.pipe(delay(1000))),
        take(120),
        filter((res: UpdateConnectionWebFormReponse) => !!res?.webformId)
      )
      .subscribe((response: UpdateConnectionWebFormReponse) => {
        this.handleUpdateConnection(response);
      });
  }

  removeScript() {
    this.headScript?.bodyScript?.remove();
    this.headScript?.remove();
  }

  async loadFinApiHeadScript(token: string): Promise<void> {
    this.removeScript();

    this.headScript = document.createElement('script');
    this.headScript.src =
      'https://webform-dist.finapi.io/latest/dist/web-form.min.js';
    this.headScript.type = 'text/javascript';

    this.headScript.addEventListener('load', () => {
      this.bodyScript = document.createElement('script');
      this.bodyScript.type = 'text/javascript';

      this.bodyScript.textContent = `
        (async () => {
          FinApiWebForm.load(
            document.querySelector("#webFormContainer"),
            {
              token: "${token}",
              targetUrl: "${environment.finApiUrl}",
              layoutConfig: "xs",
              customerSupportUrl: "https://zaver.com/de/customer-support",
              language: "de",
            },
            {
              onLoaded: function() {
                console.log("onLoaded");
              },
              onComplete: function() {
                console.log("onComplete");
                window.dispatchEvent(new CustomEvent('finish'));
              },
              onFail: function() {
                console.log("onFail");
                window.dispatchEvent(new CustomEvent('error'));
              },
              onAbort: function() {
                console.log("onAbort");
                window.dispatchEvent(new CustomEvent('abort'));
              },
              onLoadError: function() {
                console.log("onLoadError");
                window.dispatchEvent(new CustomEvent('error'));
              }
            }
          );
        })();
      `;

      document.getElementsByTagName('body')[0].appendChild(this.bodyScript);
    });

    document.getElementsByTagName('head')[0].appendChild(this.headScript);
  }

  @HostListener('window:error', ['$event'])
  logError(e: CustomEvent) {
    this.fetchUpdatedFinapiConnectionWebform();
  }

  @HostListener('window:finish')
  finishSuccessfulWizard() {
    this.loading = true;
    this.pollForFinapiReport();
  }

  @HostListener('window:abort')
  abortUnsuccessfulWizard() {
    this.fetchUpdatedFinapiConnectionWebform();
  }

  async handleCtaClick(): Promise<void> {
    this.navigationInProgress = true;
    this.location.back();
    this.navigationInProgress = false;
  }

  pollForFinapiReport(): void {
    this.pollingSubscription = this.userService
      .pollForFinapiReport(this.webFormId)
      .pipe(
        repeatWhen((obs) => obs.pipe(delay(1000))),
        take(120),
        filter((res) => !res)
      )
      .subscribe(() => this.handleUpdate());
  }

  private stopPolling() {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  async handleUpdate() {
    this.stopPolling();
    await this.userService.fetchFinApiReport(this.webFormId);
    this.navigate();
  }

  async handleUpdateConnection(
    response: UpdateConnectionWebFormReponse
  ): Promise<void> {
    if (response.webformId) {
      this.webFormId = response.webformId;
      this.stopPolling();
      await this.loadFinApiHeadScript(this.webFormId);
      this.loading = false;
    }
  }
}
